/**
 * The commits behind the Timeline's overlay (roadmap D180), in the console: the workbench's
 * `hooks/useCommitTimeline.ts` with its desktop transport (`commitTimeline.get`, each repository's
 * host reading git) swapped for the project's git history route,
 * `GET /projects/v1/:id/roots/:rootId/git/commits?since&until&limit&page` (routes/projectGit.ts,
 * read through the project's GitHub connection).
 *
 * For every repository of the project the commits in the timeline's range are read, page by page up
 * to the workbench's cap (COMMIT_TIMELINE_CAP), and turned into the overlay's `TimelineCommit`s:
 * who the work came from (the route's attribution, else the shared rule), where it stands
 * (`stage`), the task its subject names and that task's plan. The range ends where the timeline
 * does, so a poll asks the same address again: the platform answers from its cache, revalidated
 * with GitHub's ETag, and the browser revalidates with the platform's.
 *
 * Quiet when there is nothing to read: a repository that is not on GitHub, or a project with no
 * GitHub connection or no credential to read with (409 no_github / not_linked), adds no commits and
 * no error; the overlay then offers nothing. Nothing is read while `enabled` is false.
 */
import { attributeCommit, COMMIT_TIMELINE_CAP, GIT_COMMITS_PAGE_MAX, type GraphCommitPayload, type TimelineCommit } from '@aioe/shared';
import { useMemo } from 'react';

import { ApiError, routes } from '../../api/client.ts';
import { useLoad } from '../../hooks.ts';
import { planToneOf } from '../../workbench/planTones.ts';
import type { TimelineScope } from './useWorkTimeline.ts';

export interface CommitTimelineAsk {
  scope: TimelineScope;
  projectId?: string;
  from: number;
  to: number;
  enabled?: boolean;
  pollMs?: number;
}

export interface CommitTimelineData {
  commits: TimelineCommit[];
  /** Each repository's default branch, by id. */
  mainBranches: ReadonlyMap<string, string>;
  /** Each repository's remote's friendly name ("GitHub"), by id. */
  remoteLabels: ReadonlyMap<string, string>;
  /** Each commit's page on GitHub, by repository and hash. */
  urls: ReadonlyMap<string, string>;
  capped: boolean;
  /** The repositories whose answer was capped (only their newest commits came). */
  cappedRoots: ReadonlySet<string>;
  error?: string;
  loading: boolean;
}

/** Why a repository's history is not read, said by nobody: there is nothing to put on the timeline. */
const QUIET = new Set(['no_github', 'not_linked', 'unknown_root', 'not_found']);
const PAGES = Math.ceil(COMMIT_TIMELINE_CAP / GIT_COMMITS_PAGE_MAX);

/** One commit of the history route as the overlay draws it. */
export function asTimelineCommit(rootId: string, c: GraphCommitPayload): TimelineCommit {
  const attribution = c.attribution ?? attributeCommit({ author: c.author, coAuthors: c.coAuthors ?? [], ...(c.branches ? { branches: c.branches } : {}) });
  const task = c.task;
  return {
    rootId,
    hash: c.hash,
    short: c.short,
    at: c.at,
    subject: c.subject,
    author: c.author,
    attribution,
    // A commit read on no branch the platform follows is still somebody's work in progress.
    stage: c.stage ?? 'working',
    ...(task ? { task: { board: task.teamId, id: task.taskId, title: task.title } } : {}),
    ...(task?.planId ? { plan: { id: task.planId, ...(task.planName ? { name: task.planName } : {}), colorIndex: planToneOf(task.planId), teamId: task.teamId } } : {}),
  };
}

interface Read {
  commits: TimelineCommit[];
  mainBranches: Map<string, string>;
  remoteLabels: Map<string, string>;
  urls: Map<string, string>;
  cappedRoots: Set<string>;
  errors: string[];
}

async function readRoot(projectId: string, rootId: string, since: string, until: string, into: Read): Promise<void> {
  for (let page = 1; page <= PAGES; page++) {
    let answer;
    try {
      answer = await routes.gitCommits(projectId, rootId, { since, until, limit: GIT_COMMITS_PAGE_MAX, page });
    } catch (e) {
      if (e instanceof ApiError && QUIET.has(e.code)) return;
      into.errors.push(e instanceof Error ? e.message : String(e));
      return;
    }
    into.mainBranches.set(rootId, answer.repository.defaultBranch);
    into.remoteLabels.set(rootId, 'GitHub');
    for (const c of answer.commits) {
      into.commits.push(asTimelineCommit(rootId, c));
      if (c.url) into.urls.set(`${rootId}\u0000${c.hash}`, c.url);
    }
    if (!answer.more) return;
    if (page === PAGES) into.cappedRoots.add(rootId);
  }
}

const EMPTY: TimelineCommit[] = [];
const NO_NAMES: ReadonlyMap<string, string> = new Map();
const NO_ROOTS: ReadonlySet<string> = new Set();

export function useCommitTimeline(ask: CommitTimelineAsk): CommitTimelineData {
  const { projectId, from, to, pollMs = 60_000 } = ask;
  const enabled = ask.enabled !== false && !!projectId;
  // The project's repositories: every one the timeline's runs can come from.
  const project = useLoad(() => (enabled && projectId ? routes.project(projectId).catch(() => undefined) : Promise.resolve(undefined)), [enabled, projectId]);
  const roots = useMemo(() => [...new Set((project.data?.roots ?? []).map((r) => r.rootId).filter(Boolean))].sort(), [project.data]);
  const load = useLoad(
    async () => {
      if (!enabled || !projectId || !roots.length) return undefined;
      const read: Read = { commits: [], mainBranches: new Map(), remoteLabels: new Map(), urls: new Map(), cappedRoots: new Set(), errors: [] };
      const since = new Date(from).toISOString();
      const until = new Date(to).toISOString();
      await Promise.all(roots.map((rootId) => readRoot(projectId, rootId, since, until, read)));
      // Oldest first, as the desktop answers; one commit read on two branches is one commit.
      const seen = new Set<string>();
      read.commits = read.commits.filter((c) => (seen.has(`${c.rootId}\u0000${c.hash}`) ? false : (seen.add(`${c.rootId}\u0000${c.hash}`), true))).sort((a, b) => a.at - b.at);
      return read;
    },
    [enabled, projectId, roots.join('\u0000'), from, to],
    enabled && pollMs > 0 ? pollMs : undefined,
  );
  const shown = enabled ? load.data : undefined;
  return useMemo(
    () => ({
      commits: shown?.commits ?? EMPTY,
      mainBranches: shown?.mainBranches ?? NO_NAMES,
      remoteLabels: shown?.remoteLabels ?? NO_NAMES,
      urls: shown?.urls ?? NO_NAMES,
      capped: (shown?.cappedRoots.size ?? 0) > 0,
      cappedRoots: shown?.cappedRoots ?? NO_ROOTS,
      ...(shown?.errors.length ? { error: shown.errors.join(' ') } : {}),
      loading: enabled && (project.loading || load.loading),
    }),
    [shown, enabled, project.loading, load.loading],
  );
}
